import { ListItem, Typography } from "@mui/material";
import * as React from "react";
import { getExpandState } from "../../../../common/services/Utilities";
import { KeyValue, FormSelectChoice, Query } from "../../../../interfaces";
import {
  defaultSearchFieldInputLookup,
  marketDisplayResultOptions,
  marketShareReportChoicesBHCs,
  marketShareReportChoicesBanks,
  rssdRegEx,
} from "../../../../refdata/constants";
import MoreInfoButton from "../../../MoreInfoButton";
import SelectFilterField from "../../../SelectFilterField";
import { MarketShareForm } from "../MarketShareSearchForm";
import AccordionDropdown from "../../../../containers/AccordionDropdown";
import IncludeCheckbox from "../../../IncludeCheckbox";
import MultiDropdownInput from "../../../../containers/MultiDropdownInput";
import useGetStateList from "../../../../hooks/useGetStateList";
import BankBHCsToggle from "../../../BankBHCsToggle";

interface Props {
  appState: Query;
  searchForm: MarketShareForm;
  formMetaData: KeyValue;
  setSearchForm: (searchForm: MarketShareForm) => void;
  errors: Set<string>;
}

const bankFormValues = ["cert", "name"];
const bhcFormValues = ["bhcName", "bhcRSSD"];

const label: FormSelectChoice = {
  label: "All States and Territories",
  value: "ALL",
};

const marketPresenceSearchFieldInputs = {
  ...defaultSearchFieldInputLookup,
  name: {
    ...defaultSearchFieldInputLookup.name,
    apiPath: "sod",
    uniqueField: "NAMEFULL",
  },
  bhcName: {
    ...defaultSearchFieldInputLookup.bhcName,
    apiPath: "sod",
    uniqueField: "NAMEHCR",
  },
  bhcRSSD: {
    placeholder: "Enter Holding Company ID",
    label: "Bank Holding Company ID",
    apiPath: "sod",
    uniqueField: "RSSDHCR",
    regEx: rssdRegEx,
    autoComplete: false,
  },
};

export default function MarketPresenceSearchForm({
  appState,
  searchForm,
  formMetaData,
  setSearchForm,
  errors,
}: Props) {
  const stateList = useGetStateList(searchForm, setSearchForm);

  React.useEffect(() => {
    if (!searchForm.displayResults) {
      setSearchForm({
        ...searchForm,
        stalpbr: "",
        displayResults: "statesByCounty",
      });
    }
  }, [searchForm, setSearchForm]);

  return (
    <>
      <ListItem>
        <AccordionDropdown
          id="institutionType"
          title="Institution Type"
          defaultExpand={getExpandState(
            appState.ui.institutionTypeExpand,
            true,
          )}
          body={
            <BankBHCsToggle
              form={searchForm}
              setForm={setSearchForm}
              bankValues={bankFormValues}
              bhcValues={bhcFormValues}
            />
          }
        />
      </ListItem>
      <ListItem>
        {searchForm.institutionType === "bhcs" && (
          <AccordionDropdown
            id="bhcType"
            title={"RSSDHCR / BHC Name"}
            defaultExpand={getExpandState(
              appState.ui.institutionTypeExpand,
              true,
            )}
            body={
              <MultiDropdownInput
                selectId="bhcName"
                form={searchForm}
                setForm={setSearchForm}
                initialSearchFieldName={
                  appState.search.bhcName
                    ? "bhcName"
                    : appState.filters.bhcRSSD
                    ? "bhcRSSD"
                    : ""
                }
                formSelectChoices={marketShareReportChoicesBHCs}
                searchFieldInputLookup={marketPresenceSearchFieldInputs}
                errors={errors}
                formMetaData={formMetaData}
              />
            }
          />
        )}
        {searchForm.institutionType === "banks" && (
          <AccordionDropdown
            id="institutionType"
            title={"Bank Name / Cert"}
            defaultExpand={getExpandState(
              appState.ui.institutionTypeExpand,
              true,
            )}
            body={
              <MultiDropdownInput
                selectId="name-cert"
                form={searchForm}
                setForm={setSearchForm}
                initialSearchFieldName={
                  appState.search.name
                    ? "name"
                    : appState.filters.cert
                    ? "cert"
                    : ""
                }
                formSelectChoices={marketShareReportChoicesBanks}
                searchFieldInputLookup={marketPresenceSearchFieldInputs}
                errors={errors}
                formMetaData={formMetaData}
              />
            }
          />
        )}
      </ListItem>
      <ListItem>
        <AccordionDropdown
          id="displayResults"
          title="Display Results"
          defaultExpand={getExpandState(
            appState.ui.institutionTypeExpand,
            true,
          )}
          body={
            <>
              <SelectFilterField
                currentValue={
                  searchForm.displayResults
                    ? searchForm.displayResults
                    : "statesByCounty"
                }
                fieldName="displayResults"
                options={marketDisplayResultOptions}
                form={searchForm}
                setForm={setSearchForm}
              />
              {searchForm.displayResults === "statesByCounty" && (
                <SelectFilterField
                  currentValue={searchForm.stalpbr}
                  fieldName={"stalpbr"}
                  options={[label, ...stateList]}
                  form={searchForm}
                  setForm={setSearchForm}
                />
              )}
              {searchForm.displayResults !== "msas" && (
                <div
                  id="includeBranchOffices"
                  style={{ margin: "-12px 0 0 18px" }}
                >
                  <IncludeCheckbox
                    currentValue={searchForm.displayAll}
                    form={searchForm}
                    setForm={setSearchForm}
                    label="Display all States / Counties"
                    fieldName="displayAll"
                  />
                  <MoreInfoButton
                    id="dispayAllInfo"
                    popperPosition="right"
                    label="Display all States / Counties"
                    hover
                  >
                    <Typography>
                      Selecting this option will display results for all States
                      and/or Counties, depending on display type, regardless of
                      whether an institution has a presence there.
                    </Typography>
                  </MoreInfoButton>
                </div>
              )}
            </>
          }
        />
      </ListItem>
    </>
  );
}
